import { CurrentUser, UserSearchFilter, UserRef } from '@rygen/rygen-ui/model';

import {
  DoesUserHavePermissionEntityType,
  DoesUserHavePermissionResponse,
} from '@/model';

import { BaseHttpController } from './BaseHttpController';

const endpoint = 'user';

export class UserController extends BaseHttpController {
  constructor() {
    super(endpoint);
  }

  async getCurrentUser(): Promise<CurrentUser> {
    const response = await this.http.get<CurrentUser>('current', {
      params: { allowNavigations: true },
    });

    if (!response.data) {
      throw new Error('getCurrentUser response is invalid');
    }

    return new CurrentUser(response.data);
  }

  async doesCurrentUserHavePermission(
    entityType: DoesUserHavePermissionEntityType,
    entityId: number,
    permission: string,
    requiresOwner = false,
  ): Promise<boolean> {
    const response = await this.http.get<DoesUserHavePermissionResponse>(
      `current/has-permission/${entityType}/${entityId}/${permission}`,
      {
        params: {
          useSelectedDomainContext: true,
          requiresOwner,
        },
      },
    );

    return response.data.hasPermission;
  }

  async findAssignableUser(userId: number): Promise<UserRef> {
    const response = await this.http.get<UserRef>(`${userId}/assignable`);

    return new UserRef(response.data);
  }

  async findAllAssignableUsersForDomain(
    domainId: number,
    entityType: 'shipment' | 'order',
    includeInternalAssignees = false,
  ): Promise<UserRef[]> {
    const response = await this.http.get<UserRef[]>(
      `assignable/domain/${domainId}`,
      { params: { entityType, includeInternalAssignees } },
    );

    return response.data.map((val) => new UserRef(val));
  }

  async searchAssignableUsers(payload: UserSearchFilter): Promise<UserRef[]> {
    const response = await this.http.post<UserRef[]>(
      'assignable/search',
      payload,
    );

    return response.data.map((val) => new UserRef(val));
  }

  async getCreateInvoicePermission(): Promise<boolean> {
    const response = await this.http.get<boolean>('can-create-invoice');
    return response.data;
  }

  async getCreateInvoiceShipmentPermission(
    shipmentId: number,
  ): Promise<boolean> {
    const response = await this.http.get(
      `can-create-invoice/shipment/${shipmentId}`,
    );
    return response.data;
  }

  async getHasUpliftPermission(): Promise<boolean> {
    const response = await this.http.get<boolean>('view-uplift');
    return response.data;
  }

  async getHasUpliftShipmentPermission(shipmentId: number): Promise<boolean> {
    const response = await this.http.get<boolean>(
      `view-uplift/shipment/${shipmentId}`,
    );
    return response.data;
  }

  async getHasUpliftOrderPermission(orderId: number): Promise<boolean> {
    const response = await this.http.get<boolean>(
      `view-uplift/order/${orderId}`,
    );
    return response.data;
  }

  async getCanCreateFinancialSettingPermission(
    shipmentId: number,
  ): Promise<boolean> {
    const response = await this.http.get<boolean>(
      `create-financial-setting/shipment/${shipmentId}`,
    );
    return response.data;
  }
}
